/**
 * Edge Properties Panel
 * Shows and edits properties when a connection is selected
 */

import React from 'react';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { LinkIcon, ThemedSelect } from '@bizfirst/common-react';

const EDGE_TYPES = ['default', 'straight', 'step', 'smoothstep', 'smart'] as const;
const MARKER_TYPES = ['', 'arrow', 'arrowclosed'] as const;
const MARKER_LABELS: Record<string, string> = { '': 'None', arrow: 'Arrow', arrowclosed: 'Arrow (Closed)' };

export const EdgeProperties: React.FC = () => {
  const { edges, updateEdge } = useWorkflowStore();
  const { selectedEdges, setSelectedEdges, theme } = useUIStore();

  const selectedEdge = edges.find(edge => selectedEdges.includes(edge.id));

  if (!selectedEdge) return null;

  const handleClose = () => setSelectedEdges([]);

  const set = (updates: Record<string, any>) => updateEdge(selectedEdge.id, updates as any);

  const setStyle = (key: string, value: any) =>
    set({ style: { ...(selectedEdge.style || {}), [key]: value } });

  const setLabelStyle = (key: string, value: any) =>
    set({ labelStyle: { ...(selectedEdge.labelStyle || {}), [key]: value } });

  const getMarkerValue = (marker: any): string => {
    if (!marker) return '';
    if (typeof marker === 'string') return marker;
    return (marker as any).type || '';
  };

  const setMarker = (field: 'markerEnd' | 'markerStart', value: string) =>
    set({ [field]: value || undefined });

  const isDark = theme === 'dark';
  const themeClass = isDark ? '' : 'light';

  const ctrl: React.CSSProperties = {
    width: '100%',
    fontSize: '13px',
    color: isDark ? 'rgba(220, 225, 222, 0.9)' : '#101211',
    background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f9fafb',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.07)' : '#e5e7eb'}`,
    borderRadius: '4px',
    padding: '4px 8px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const colorCtrl: React.CSSProperties = {
    ...ctrl,
    width: '48px',
    height: '28px',
    padding: '2px 4px',
    cursor: 'pointer',
  };

  const numCtrl: React.CSSProperties = { ...ctrl, cursor: 'text' };

  const strokeColor = (selectedEdge.style as any)?.stroke || '';
  const strokeWidth = (selectedEdge.style as any)?.strokeWidth ?? '';
  const labelColor = (selectedEdge.labelStyle as any)?.color || '';
  const labelFontSize = (selectedEdge.labelStyle as any)?.fontSize ?? '';

  return (
    <div className={`properties-panel ${themeClass}`}>
      <div className="properties-header">
        <div className="properties-header-content">
          <div className="properties-title"><LinkIcon size={14} /> Connection Properties</div>
          <div className="properties-subtitle">Edge: {selectedEdge.id}</div>
        </div>
        <button className="properties-close-button" onClick={handleClose} title="Close">✕</button>
      </div>

      <div className="properties-body">

        {/* Read-only routing info */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Source</div>
          <div className={`property-value ${themeClass}`}>{selectedEdge.source} → {selectedEdge.sourceHandle || 'main'}</div>
        </div>
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Target</div>
          <div className={`property-value ${themeClass}`}>{selectedEdge.target} → {selectedEdge.targetHandle || 'main'}</div>
        </div>

        {/* Connection Type */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Connection Type</div>
          <ThemedSelect
            ariaLabel="Connection type"
            value={selectedEdge.type || 'default'}
            onChange={value => set({ type: value })}
            options={EDGE_TYPES.map(t => ({ value: t, label: t }))}
          />
        </div>

        {/* Animated */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Animated</div>
          <ThemedSelect
            ariaLabel="Animated"
            value={selectedEdge.animated ? 'yes' : 'no'}
            onChange={value => set({ animated: value === 'yes' })}
            options={[{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]}
          />
        </div>

        {/* Label */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Label</div>
          <input
            type="text"
            style={{ ...ctrl, cursor: 'text' }}
            value={(selectedEdge.label as string) || ''}
            placeholder="No label"
            onChange={e => set({ label: e.target.value || undefined })}
          />
        </div>

        {/* Label Show Background */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Label Show Background</div>
          <ThemedSelect
            ariaLabel="Label show background"
            value={selectedEdge.labelShowBg === false ? 'no' : 'yes'}
            onChange={value => set({ labelShowBg: value === 'yes' })}
            options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
          />
        </div>

        {/* Label Color */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Label Color</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="color" style={colorCtrl} value={labelColor || '#ffffff'} onChange={e => setLabelStyle('color', e.target.value)} />
            <input type="text" style={{ ...ctrl, flex: 1, cursor: 'text' }} value={labelColor} placeholder="inherit" onChange={e => setLabelStyle('color', e.target.value || undefined)} />
          </div>
        </div>

        {/* Label Font Size */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Label Font Size</div>
          <input
            type="number"
            style={numCtrl}
            value={labelFontSize}
            placeholder="12"
            min={8}
            max={32}
            onChange={e => setLabelStyle('fontSize', e.target.value ? Number(e.target.value) : undefined)}
          />
        </div>

        {/* Line Color */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Line Color</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="color" style={colorCtrl} value={strokeColor || '#888888'} onChange={e => setStyle('stroke', e.target.value)} />
            <input type="text" style={{ ...ctrl, flex: 1, cursor: 'text' }} value={strokeColor} placeholder="default" onChange={e => setStyle('stroke', e.target.value || undefined)} />
          </div>
        </div>

        {/* Line Width */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Line Width</div>
          <input
            type="number"
            style={numCtrl}
            value={strokeWidth}
            placeholder="1"
            min={0.5}
            max={10}
            step={0.5}
            onChange={e => setStyle('strokeWidth', e.target.value ? Number(e.target.value) : undefined)}
          />
        </div>

        {/* Marker End */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Marker End</div>
          <ThemedSelect
            ariaLabel="Marker end"
            value={getMarkerValue(selectedEdge.markerEnd)}
            onChange={value => setMarker('markerEnd', value)}
            options={MARKER_TYPES.map(m => ({ value: m, label: MARKER_LABELS[m] }))}
          />
        </div>

        {/* Marker Start */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Marker Start</div>
          <ThemedSelect
            ariaLabel="Marker start"
            value={getMarkerValue(selectedEdge.markerStart)}
            onChange={value => setMarker('markerStart', value)}
            options={MARKER_TYPES.map(m => ({ value: m, label: MARKER_LABELS[m] }))}
          />
        </div>

        {/* Z-Index — read-only */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Z-Index</div>
          <div className={`property-value ${themeClass}`}>{selectedEdge.zIndex ?? 'auto'}</div>
        </div>

        {/* Hidden — read-only */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Hidden</div>
          <div className={`property-value ${themeClass}`}>{selectedEdge.hidden ? 'Yes' : 'No'}</div>
        </div>

        {/* Deletable — read-only */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Deletable</div>
          <div className={`property-value ${themeClass}`}>{(selectedEdge as any).deletable === false ? 'No' : 'Yes'}</div>
        </div>

        {/* Selectable — read-only */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Selectable</div>
          <div className={`property-value ${themeClass}`}>{(selectedEdge as any).selectable === false ? 'No' : 'Yes'}</div>
        </div>

        {/* Class Name */}
        <div className="property-row">
          <div className={`property-label ${themeClass}`}>Class Name</div>
          <input
            type="text"
            style={{ ...ctrl, cursor: 'text' }}
            value={selectedEdge.className || ''}
            placeholder="none"
            onChange={e => set({ className: e.target.value || undefined })}
          />
        </div>

      </div>
    </div>
  );
};
